import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
  MenuItem,
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
} from "@material-ui/core";

import EditIcon from "@material-ui/icons/Edit";
import { useAlert } from "react-alert";

import { AuctionSetupUpdate } from "../../../adminModel/auctions";


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: "22px 24px 16px",
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  titleIcon: {
    fontSize: 22,
    color: theme.palette.primary.main,
  },

  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#202124",
  },

  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: "#6b7280",
  },

  content: {
    padding: "8px 24px 24px",
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 16,
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 7,
      backgroundColor: "#fff",
    },

    "& .MuiInputLabel-root": {
      fontSize: 14,
    },

    "& .MuiInputBase-input": {
      fontSize: 14,
    },
  },

  actions: {
    padding: "14px 24px",
    backgroundColor: "#fafafa",
  },

  closeButton: {
    textTransform: "none",
    borderRadius: 7,
    minWidth: 90,
  },

  updateButton: {
    textTransform: "none",
    borderRadius: 7,
    minWidth: 100,
    fontWeight: 600,
  },
}));


// =====================================================
// COMPONENT
// =====================================================

export default function UpdateAuctionDialog(props) {
  const classes = useStyles();
  const alert = useAlert();

  const {
    status,
    onClose,
    dataData,
    loadingData,
  } = props;


  // =====================================================
  // STATE
  // =====================================================

  const [values, setValues] = useState({
    set_name: "",
    date_start: "",
    time_start: "",
    date_end: "",
    time_end: "",
    isLive: "",
    isShow: "",
  });

  const [saving, setSaving] = useState(false);


  // =====================================================
  // LOAD DATA WHEN DIALOG OPENS
  // =====================================================

  useEffect(() => {
    if (!dataData) {
      return;
    }

    setValues({
      set_name: dataData.set_name || "",

      date_start: dataData.date_start
        ? dataData.date_start.slice(0, 10)
        : "",

      time_start: dataData.time_start || "",

      date_end: dataData.date_end
        ? dataData.date_end.slice(0, 10)
        : "",

      time_end: dataData.time_end || "",

      isLive:
        dataData.isLive !== undefined &&
        dataData.isLive !== null
          ? String(dataData.isLive)
          : "",

      isShow:
        dataData.isShow !== undefined &&
        dataData.isShow !== null
          ? String(dataData.isShow)
          : "",
    });
  }, [dataData, status]);


  // =====================================================
  // HANDLE CHANGE
  // =====================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  // =====================================================
  // CLOSE
  // =====================================================

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose(false);
  };


  // =====================================================
  // UPDATE
  // =====================================================

  const handleUserUpdate = async (e) => {
    e.preventDefault();

    if (!dataData) {
      return;
    }

    // Basic validation
    if (!values.set_name.trim()) {
      alert.error("Auction name is required.");
      return;
    }

    if (!values.date_start || !values.time_start) {
      alert.error("Start date and time are required.");
      return;
    }

    if (!values.date_end || !values.time_end) {
      alert.error("End date and time are required.");
      return;
    }

    const startDateTime = new Date(
      `${values.date_start}T${values.time_start}`
    );

    const endDateTime = new Date(
      `${values.date_end}T${values.time_end}`
    );

    if (endDateTime < startDateTime) {
      alert.error("End date/time cannot be before start date/time.");
      return;
    }

    const UpdateData = {
      set_name: values.set_name,
      date_start: values.date_start,
      time_start: values.time_start,
      date_end: values.date_end,
      time_end: values.time_end,
      isLive: Number(values.isLive),
      isShow: Number(values.isShow),
    };

    setSaving(true);
    loadingData(true);

    try {
      const result = await AuctionSetupUpdate(
        dataData.id,
        UpdateData
      );

      alert.success(result);

      onClose(false);
    } catch (error) {
      console.error("Auction update error:", error);

      alert.error(
        error?.response?.data?.msg ||
          "Unable to update auction."
      );
    } finally {
      setSaving(false);
      loadingData(false);
    }
  };


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={Boolean(status)}
      onClose={handleClose}
      aria-labelledby="update-auction-dialog-title"
    >

      {/* =================================================
          HEADER
      ================================================= */}

      <Box className={classes.dialogTitle}>

        <Box className={classes.titleRow}>

          <EditIcon className={classes.titleIcon} />

          <Typography
            id="update-auction-dialog-title"
            className={classes.title}
          >
            Edit Auction Setup
          </Typography>

        </Box>

        <Typography className={classes.subtitle}>
          Update the auction schedule and visibility settings.
        </Typography>

      </Box>

      <Divider />


      {/* =================================================
          CONTENT
      ================================================= */}

      <DialogContent className={classes.content}>

        <Box mt={1} mb={3}>

          <Typography className={classes.sectionTitle}>
            Auction Information
          </Typography>

          <Grid container spacing={3}>

            {/* NAME */}

            <Grid item xs={12}>

              <TextField
                className={classes.field}
                fullWidth
                required
                label="Auction Set Name"
                name="set_name"
                type="text"
                variant="outlined"
                value={values.set_name}
                onChange={handleChange}
                disabled={saving}
              />

            </Grid>


            {/* START DATE */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                label="Start Date"
                name="date_start"
                type="date"
                variant="outlined"
                value={values.date_start}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={saving}
              />

            </Grid>


            {/* START TIME */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                label="Start Time"
                name="time_start"
                type="time"
                variant="outlined"
                value={values.time_start}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={saving}
              />

            </Grid>


            {/* END DATE */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                label="End Date"
                name="date_end"
                type="date"
                variant="outlined"
                value={values.date_end}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={saving}
              />

            </Grid>


            {/* END TIME */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                label="End Time"
                name="time_end"
                type="time"
                variant="outlined"
                value={values.time_end}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={saving}
              />

            </Grid>

          </Grid>

        </Box>


        <Divider />


        {/* =================================================
            SETTINGS
        ================================================= */}

        <Box mt={3}>

          <Typography className={classes.sectionTitle}>
            Auction Settings
          </Typography>

          <Grid container spacing={3}>

            {/* LIVE */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                select
                label="Live Auction"
                name="isLive"
                variant="outlined"
                value={values.isLive}
                onChange={handleChange}
                disabled={saving}
              >

                <MenuItem value="1">
                  Yes
                </MenuItem>

                <MenuItem value="0">
                  No
                </MenuItem>

              </TextField>

            </Grid>


            {/* SHOW */}

            <Grid item xs={12} sm={6}>

              <TextField
                className={classes.field}
                fullWidth
                required
                select
                label="Show Auction"
                name="isShow"
                variant="outlined"
                value={values.isShow}
                onChange={handleChange}
                disabled={saving}
              >

                <MenuItem value="1">
                  Yes
                </MenuItem>

                <MenuItem value="0">
                  No
                </MenuItem>

              </TextField>

            </Grid>

          </Grid>

        </Box>

      </DialogContent>


      {/* =================================================
          ACTIONS
      ================================================= */}

      <Divider />

      <DialogActions className={classes.actions}>

        <Button
          className={classes.closeButton}
          variant="outlined"
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          className={classes.updateButton}
          variant="contained"
          color="primary"
          onClick={handleUserUpdate}
          disabled={saving}
        >
          {saving ? "Updating..." : "Update Auction"}
        </Button>

      </DialogActions>

    </Dialog>
  );
}